import { useEffect, useState, useMemo, useRef, useCallback, useContext } from "react";
import ToggleSwitch from "../../../CommonComponents/ToggleSwitch/ToggleSwitch";

import "./stylesPaymentToggle.css";
import createPrivateAdminPanelInstance from "../../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../../api/ApiRoutes";
import SaveButton from "../../../CommonComponents/SaveButton/SaveButton";
import { message } from "antd";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { useSelector } from "react-redux";
import RebetLoader from "../../../CommonComponents/RebetLoader/RebetLoader";
import { AGENT_ACTIONS } from "../../../../utils/Constants";
import AppContext from "../../../../context/AppContext";
import ApplePayV2Routing from "../ApplePayV2Routing/ApplePayV2Routing";
import { useProductFeatureFlag } from "../../../../hooks/useFeatureFlag";
import { formatProviderLabel } from "../../../../utils/formatProviderLabel";

// Card Payments: exclude Evervault and NMI (BE does not allow routing for those on card payments)
const CARD_EXCLUDED_PROVIDERS = ["evervault", "nmi"];

const PaymentToggle = () => {
  const token = JSON.parse(localStorage.getItem("token"));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.AccessToken,
  );

  const appConfig = useSelector((state) => state.config.appConfig);
  const paymentProviders = appConfig?.features_status_config?.payment_providers;

  const { adminActions } = useContext(AppContext);
  const canEditTransactionRouting = adminActions?.includes(AGENT_ACTIONS?.EDIT_TRANSACTION_ROUTING);

  // The range-based ApplePayV2Routing UI ships behind the `apple_pay_routing`
  // flag (v2 platform config). Until it's on, the legacy threshold UI stays.
  const {
    isEnabled: isApplePayV2RoutingEnabled,
    loading: applePayFlagLoading,
  } = useProductFeatureFlag("apple_pay_routing");

  const [isApiRunning, setIsApiRunning] = useState(false);
  // Dynamic state for provider enabled status
  const [enabledProviders, setEnabledProviders] = useState({});
  const [apiEnabledProviders, setApiEnabledProviders] = useState([]);

  // States for 2 processors mode
  const [thresholdLow, setThresholdLow] = useState("");
  const [lowerProcessor, setLowerProcessor] = useState("");
  const [upperProcessor, setUpperProcessor] = useState("");
  const [condition0, setCondition0] = useState("less");
  const [condition1, setCondition1] = useState("greater");

  // Dynamic routing rules for 3+ processors
  // Each rule: { processor: string, threshold: string }
  const [routingRules, setRoutingRules] = useState([]);

  const [_successMessage, setSuccessMessage] = useState("");
  const [_errorMessage, setErrorMessage] = useState("");

  // Tab: Card Payments | Apple Pay (separate routing flows)
  const [activeRoutingTab, setActiveRoutingTab] = useState("cards");

  // Apple Pay routing (only Finix and NMI; uses apple-pay/settings API)
  const [applePayThreshold, setApplePayThreshold] = useState("15");
  const [applePayProviderBelow, setApplePayProviderBelow] = useState("nmi");
  const [applePayProviderAbove, setApplePayProviderAbove] = useState("finix");
  // Starts true so the legacy editor never paints its hard-coded defaults
  // before the settings fetch kicks in (mirrors cardSettingsLoaded).
  const [isApplePayLoading, setIsApplePayLoading] = useState(true);
  const [isApplePaySaving, setIsApplePaySaving] = useState(false);
  const [_applePaySuccessMessage, setApplePaySuccessMessage] = useState("");
  const [_applePayErrorMessage, setApplePayErrorMessage] = useState("");

  // Card settings loaded from API (avoid showing all toggles as on before API returns)
  const [cardSettingsLoaded, setCardSettingsLoaded] = useState(false);

  // Track unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const initialDataLoaded = useRef(false);
  const alertShown = useRef(false);

  // Initialize enabled providers from paymentProviders config
  useEffect(() => {
    if (paymentProviders && typeof paymentProviders === "object") {
      setEnabledProviders(paymentProviders);
    }
  }, [paymentProviders]);

  // Alert user when mouse leaves the container with unsaved changes (only once)
  const handleMouseLeave = useCallback(() => {
    if (isApiRunning) {
      return;
    }

    if (hasUnsavedChanges && !alertShown.current) {
      alertShown.current = true;
      message.warning(AppStrings.PAM_UNSAVED_CHANGES_WARNING);
    }
  }, [hasUnsavedChanges, isApiRunning]);

  // Mark changes as unsaved when user modifies settings
  const markAsChanged = useCallback(() => {
    if (initialDataLoaded.current) {
      setHasUnsavedChanges(true);
    }
  }, []);

  // Get list of available providers (keys from paymentProviders)
  const featureFlagEnabledProviders = useMemo(() => {
    if (!paymentProviders || typeof paymentProviders !== "object") {
      return null;
    }

    const entries = Object.entries(paymentProviders);
    if (!entries.length) {
      return null;
    }

    return new Set(
      entries
        .filter(([, isEnabled]) => isEnabled === true)
        .map(([code]) => code),
    );
  }, [paymentProviders]);

  const availableProviders = useMemo(() => {
    if (Array.isArray(apiEnabledProviders) && apiEnabledProviders.length > 0) {
      const visibleApiProviders = featureFlagEnabledProviders
        ? apiEnabledProviders.filter((code) =>
            featureFlagEnabledProviders.has(code),
          )
        : apiEnabledProviders;

      return visibleApiProviders.map((code) => ({
        code,
        label: formatProviderLabel(code),
      }));
    }

    if (featureFlagEnabledProviders) {
      return Array.from(featureFlagEnabledProviders).map((code) => ({
        code,
        label: formatProviderLabel(code),
      }));
    }

    return Object.keys(enabledProviders)
      .filter((code) => enabledProviders[code] === true)
      .map((code) => ({
        code,
        label: formatProviderLabel(code),
      }));
  }, [apiEnabledProviders, featureFlagEnabledProviders, enabledProviders]);

  const cardProvidersFiltered = useMemo(() => {
    return availableProviders.filter(
      (p) => !CARD_EXCLUDED_PROVIDERS.includes(p.code.toLowerCase()),
    );
  }, [availableProviders]);

  const updateEventCode = ({ event_code, is_active }) => {
    setEnabledProviders((prev) => ({
      ...prev,
      [event_code]: is_active,
    }));
    markAsChanged();
  };

  // Active processors for Card Payments (excludes Evervault, NMI)
  const cardActiveProcessors = useMemo(() => {
    return cardProvidersFiltered.filter(
      (provider) => enabledProviders[provider.code] === true,
    );
  }, [cardProvidersFiltered, enabledProviders]);

  const activeProcessorCodes = cardActiveProcessors.map(
    (processor) => processor.code,
  );
  const twoProcessorsEnabled = activeProcessorCodes.length === 2;
  // 3 or more processors enabled
  const threeOrMoreProcessorsEnabled = activeProcessorCodes.length >= 3;

  const savePaymentProcessor = async () => {
    setErrorMessage("");
    setSuccessMessage("");

    if (activeProcessorCodes.length === 0) {
      const msg = "Please enable at least one payment processor.";
      setErrorMessage(msg);
      message.error(msg);
      return;
    }

    if (twoProcessorsEnabled && thresholdLow === "") {
      const msg = "Please set a threshold amount to route transactions.";
      setErrorMessage(msg);
      message.error(msg);
      return;
    }

    if (threeOrMoreProcessorsEnabled) {
      const thresholds = routingRules.slice(0, -1).map((r) => r.threshold);
      if (thresholds.some((t) => t === "")) {
        const msg = "Please set all threshold amounts to route transactions.";
        setErrorMessage(msg);
        message.error(msg);
        return;
      }

      const numericThresholds = thresholds.map(Number);
      for (let i = 1; i < numericThresholds.length; i++) {
        if (numericThresholds[i] <= numericThresholds[i - 1]) {
          const msg = "Threshold amounts must be in ascending order.";
          setErrorMessage(msg);
          message.error(msg);
          return;
        }
      }
    }

    setIsApiRunning(true);

    let routingRulesPayload = [];

    if (activeProcessorCodes.length === 1) {
      routingRulesPayload = [
        {
          processor: activeProcessorCodes[0],
          lower_bound: null,
          upper_bound: null,
        },
      ];
    } else if (twoProcessorsEnabled) {
      const lowerProcessorCode =
        condition0 === "less" ? lowerProcessor : upperProcessor;
      const upperProcessorCode =
        condition1 === "greater" ? upperProcessor : lowerProcessor;

      routingRulesPayload = [
        {
          processor: lowerProcessorCode,
          lower_bound: null,
          upper_bound: thresholdLow,
        },
        {
          processor: upperProcessorCode,
          lower_bound: thresholdLow,
          upper_bound: null,
        },
      ];
    } else {
      // Build dynamic routing rules for 3+ processors
      routingRulesPayload = routingRules.map((rule, index) => {
        const isFirst = index === 0;
        const isLast = index === routingRules.length - 1;

        return {
          processor: rule.processor,
          lower_bound: isFirst ? null : routingRules[index - 1].threshold,
          upper_bound: isLast ? null : rule.threshold,
        };
      });
    }

    try {
      await PrivateAdminPanelInstance.patch(
        API_ROUTES.UPDATE_PAYMENT_ROUTING_SETTINGS,
        { routing_rules: routingRulesPayload },
      );

      const msg = "Payment processor settings saved successfully.";
      setSuccessMessage(msg);
      message.success(msg);
      setHasUnsavedChanges(false);
      alertShown.current = false;
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      const msg = error?.response?.data?.message ?? "Failed to save settings.";
      setErrorMessage(msg);
      message.error(msg);
    } finally {
      setIsApiRunning(false);
    }
  };

  const handleSelectCondition = (
    condition,
    setCondition,
    setOtherCondition,
  ) => {
    setCondition(condition);
    setOtherCondition(condition === "greater" ? "less" : "greater");
  };

  const handleSelectCondition0 = (value) => {
    handleSelectCondition(value, setCondition0, setCondition1);
    markAsChanged();
  };

  const handleSelectCondition1 = (value) => {
    handleSelectCondition(value, setCondition1, setCondition0);
    markAsChanged();
  };

  const handleThresholdLowChange = (value) => {
    setThresholdLow(value);
    markAsChanged();
  };

  const handleLowerProcessorChange = (value) => {
    setLowerProcessor(value);
    markAsChanged();
  };

  const handleUpperProcessorChange = (value) => {
    setUpperProcessor(value);
    markAsChanged();
  };

  // Sync processor selections when enabled providers change
  useEffect(() => {
    const enabledCodes = cardActiveProcessors.map((p) => p.code);

    if (enabledCodes.length) {
      // For 2 processors mode
      if (!enabledCodes.includes(lowerProcessor)) {
        setLowerProcessor(enabledCodes[0]);
      }
      if (!enabledCodes.includes(upperProcessor)) {
        setUpperProcessor(enabledCodes[1] || enabledCodes[0]);
      }

      // For 3+ processors mode - initialize routing rules
      if (enabledCodes.length >= 3) {
        // Create a rule for each active processor
        const newRules = enabledCodes.map((code, index) => {
          // Try to preserve existing threshold values
          const existingRule = routingRules[index];
          return {
            processor: code,
            threshold: existingRule?.threshold || "",
          };
        });
        setRoutingRules(newRules);
      }
    }
  // Intentionally only depend on cardActiveProcessors; including lowerProcessor/upperProcessor/routingRules would cause an infinite loop (effect sets them).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardActiveProcessors]);

  const getPaymentProcessor = useCallback(async () => {
    setIsApiRunning(true);

    try {
      const response = await PrivateAdminPanelInstance.get(
        API_ROUTES.GET_PAYMENT_ROUTING_SETTINGS,
      );
      const payload = response?.data || {};
      const data = payload?.data || {};
      const enabledProvidersList = Array.isArray(payload?.enabled_providers)
        ? payload.enabled_providers
        : [];
      const defaultEnabledState = enabledProvidersList.reduce(
        (acc, code) => ({ ...acc, [code]: false }),
        {},
      );

      if (enabledProvidersList.length > 0) {
        setApiEnabledProviders(enabledProvidersList);
        setEnabledProviders((prev) => ({
          ...prev,
          ...defaultEnabledState,
        }));
      }

      const apiRoutingRules = Array.isArray(data.routing_rules)
        ? data.routing_rules
        : [];

      if (apiRoutingRules.length) {
        // Load dynamic routing rules from API
        const loadedRules = apiRoutingRules.map((rule) => ({
          processor: rule?.processor || "",
          threshold: rule?.upper_bound ?? "",
        }));
        setRoutingRules(loadedRules);

        // For 2 processors mode compatibility
        if (apiRoutingRules.length >= 1) {
          setThresholdLow(apiRoutingRules[0]?.upper_bound ?? "");
        }

        const processors = apiRoutingRules
          .map((rule) => rule?.processor)
          .filter(Boolean);

        // Enable processors from routing rules
        setEnabledProviders((prev) => {
          const updatedEnabled = {
            ...prev,
            ...defaultEnabledState,
          };
          processors.forEach((code) => {
            updatedEnabled[code] = true;
          });
          return updatedEnabled;
        });

        if (processors[0]) {
          setLowerProcessor(processors[0]);
        }

        if (processors[1]) {
          setUpperProcessor(processors[1]);
        }

        return;
      }

      // No routing_rules from card API - leave defaults empty
    } catch (error) {
      console.error("error getting payment processor.", error);
    } finally {
      setIsApiRunning(false);
      initialDataLoaded.current = true;
      setCardSettingsLoaded(true);
    }
  }, []);

  useEffect(() => {
    getPaymentProcessor();
  }, [getPaymentProcessor]);

  const getApplePaySettings = useCallback(async () => {
    if (!API_ROUTES.GET_APPLE_PAY_ROUTING_SETTINGS) return;
    const instance = createPrivateAdminPanelInstance(
      JSON.parse(localStorage.getItem("token"))?.AccessToken,
    );
    setIsApplePayLoading(true);
    setApplePayErrorMessage("");
    try {
      const response = await instance.get(
        API_ROUTES.GET_APPLE_PAY_ROUTING_SETTINGS,
      );
      const payload = response?.data || {};
      const data = payload?.data ?? payload;
      const threshold =
        data?.apple_pay_provider_threshold ??
        data?.updated_threshold_value ??
        data?.threshold ??
        "15";
      const processors = Array.isArray(data?.apple_pay_payment_processor)
        ? data.apple_pay_payment_processor
        : ["nmi", "finix"];
      const below = processors[0] ?? "nmi";
      const above = processors[1] ?? "finix";
      setApplePayThreshold(String(threshold));
      setApplePayProviderBelow(below);
      setApplePayProviderAbove(above);
    } catch (error) {
      console.error("error getting Apple Pay routing settings.", error);
      setApplePayErrorMessage(
        error?.response?.data?.message ??
          "Failed to load Apple Pay routing settings.",
      );
    } finally {
      setIsApplePayLoading(false);
    }
  }, []);

  const saveApplePayRouting = async () => {
    if (Number.isNaN(Number(applePayThreshold)) || !applePayThreshold) {
      const msg = "Please set a threshold amount.";
      setApplePayErrorMessage(msg);
      message.error(msg);
      return;
    }
    setIsApplePaySaving(true);
    setApplePayErrorMessage("");
    setApplePaySuccessMessage("");
    try {
      await PrivateAdminPanelInstance.patch(
        API_ROUTES.UPDATE_APPLE_PAY_ROUTING_SETTINGS,
        {
          routing_rules: null,
          updated_threshold_value: String(applePayThreshold),
          apple_pay_payment_processor: [
            applePayProviderBelow,
            applePayProviderAbove,
          ],
        },
      );
      const msg = "Apple Pay routing settings saved successfully.";
      setApplePaySuccessMessage(msg);
      message.success(msg);
      setTimeout(() => setApplePaySuccessMessage(""), 3000);
    } catch (error) {
      const res = error?.response?.data;
      const detail = res?.detail;
      const detailMsg =
        (Array.isArray(detail) && detail[0]?.msg) ??
        (typeof detail === "string" ? detail : undefined);
      const errMsg =
        res?.message ?? detailMsg ?? "Failed to save Apple Pay routing settings.";
      setApplePayErrorMessage(errMsg);
      message.error(errMsg);
    } finally {
      setIsApplePaySaving(false);
    }
  };

  useEffect(() => {
    // Skip the legacy threshold fetch once ApplePayV2Routing takes over
    // (or while we don't yet know which UI to show).
    if (applePayFlagLoading || isApplePayV2RoutingEnabled) {
      return;
    }
    getApplePaySettings();
  }, [getApplePaySettings, applePayFlagLoading, isApplePayV2RoutingEnabled]);

  const applePayProviderOptions = [
    { code: "nmi", label: "NMI" },
    { code: "finix", label: "Finix" },
  ];

  return (
    <div
      className={`payment-toggle-container-wrapper ${isApiRunning ? "loading" : ""}`}
      data-testid="payment-toggle-wrapper"
      onMouseLeave={handleMouseLeave}
    >
      <div className="payment-toggle-container">
        <div className="routing-tabs">
          <button
            type="button"
            className={`routing-tab ${activeRoutingTab === "cards" ? "active" : ""}`}
            onClick={() => setActiveRoutingTab("cards")}
          >
            {AppStrings.CARD_PAYMENTS}
          </button>
          <button
            type="button"
            className={`routing-tab ${activeRoutingTab === "apple-pay" ? "active" : ""}`}
            onClick={() => setActiveRoutingTab("apple-pay")}
          >
            {AppStrings.APPLE_PAY}
          </button>
        </div>

        {activeRoutingTab === "cards" && (
          <>
            {!cardSettingsLoaded ? (
              <div className="loading-container">
              <RebetLoader height="100px" width="100px" />
              </div>
            ) : (
              <>
                <h2>{AppStrings.PAYMENT_PROCESSOR}</h2>

                {/* Card Payments: exclude Evervault and NMI per AC */}
                {cardProvidersFiltered.map((provider) => (
          <div key={provider.code} className="toggle-section">
            <label>{provider.label}</label>
            <ToggleSwitch
              status={enabledProviders[provider.code] === true}
              eventCode={provider.code}
              updateEventCode={updateEventCode}
              disabled={!canEditTransactionRouting}
            />
          </div>
        ))}

        {twoProcessorsEnabled && (
          <div className="both-settings">
            <div className="condition-section">
              <label>{AppStrings.IF_PURCHASE_IS}</label>
              <select
                value={condition0}
                onChange={(e) => handleSelectCondition0(e.target.value)}
                disabled={!canEditTransactionRouting}
              >
                <option value="less">{AppStrings.LESS}</option>
                <option value="greater">{AppStrings.GREATER}</option>
              </select>
              <label>{AppStrings.THAN}</label>
              <input
                type="number"
                value={thresholdLow}
                onChange={(e) => handleThresholdLowChange(e.target.value)}
                placeholder={AppStrings.AMOUNT}
                disabled={!canEditTransactionRouting}
              />
              <label>,</label>
              <label>{AppStrings.PAYMENT_IS_PROCESSED_BY}</label>
              <select
                value={lowerProcessor}
                onChange={(e) => handleLowerProcessorChange(e.target.value)}
                disabled={!canEditTransactionRouting}
              >
                {cardActiveProcessors.map((processor) => (
                  <option key={processor.code} value={processor.code}>
                    {processor.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="condition-section">
              <label>{AppStrings.AND_IF_PURCHASE_IS}</label>
              <select
                value={condition1}
                onChange={(e) => handleSelectCondition1(e.target.value)}
                disabled={!canEditTransactionRouting}
              >
                <option value="greater">{AppStrings.GREATER}</option>
                <option value="less">{AppStrings.LESS}</option>
              </select>
              <label>{AppStrings.THAN}</label>
              <input
                type="number"
                value={thresholdLow}
                onChange={(e) => handleThresholdLowChange(e.target.value)}
                placeholder={AppStrings.AMOUNT}
                disabled={!canEditTransactionRouting}
              />
              <label>,</label>
              <label>{AppStrings.PAYMENT_IS_PROCESSED_BY}</label>
              <select
                value={upperProcessor}
                onChange={(e) => handleUpperProcessorChange(e.target.value)}
                disabled={!canEditTransactionRouting}
              >
                {cardActiveProcessors.map((processor) => (
                  <option key={processor.code} value={processor.code}>
                    {processor.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
        {threeOrMoreProcessorsEnabled && (
          <div className="triple-settings">
            {routingRules.map((rule, index) => {
              const isFirst = index === 0;
              const isLast = index === routingRules.length - 1;
              const prevThreshold =
                index > 0 ? routingRules[index - 1].threshold : null;

              // Update rule processor
              const handleProcessorChange = (newProcessor) => {
                setRoutingRules((prev) =>
                  prev.map((r, i) =>
                    i === index ? { ...r, processor: newProcessor } : r,
                  ),
                );
                markAsChanged();
              };

              // Update rule threshold
              const handleThresholdChange = (newThreshold) => {
                setRoutingRules((prev) =>
                  prev.map((r, i) =>
                    i === index ? { ...r, threshold: newThreshold } : r,
                  ),
                );
                markAsChanged();
              };

              return (
                <div key={index} className="rule-row">
                  {isFirst && (
                    <>
                      <label>{AppStrings.TRANSACTIONS_UNDER}</label>
                      <input
                        type="number"
                        value={rule.threshold}
                        onChange={(e) => handleThresholdChange(e.target.value)}
                        placeholder={`Amount ${index + 1}`}
                        disabled={!canEditTransactionRouting}
                      />
                    </>
                  )}
                  {!isFirst && !isLast && (
                    <>
                      <label>{AppStrings.TRANSACTIONS_BETWEEN}</label>
                      <input
                        type="number"
                        value={prevThreshold}
                        disabled
                        placeholder="Previous"
                      />
                      <label>{AppStrings.AND}</label>
                      <input
                        type="number"
                        value={rule.threshold}
                        onChange={(e) => handleThresholdChange(e.target.value)}
                        placeholder={`Amount ${index + 1}`}
                        disabled={!canEditTransactionRouting}
                      />
                    </>
                  )}
                  {isLast && (
                    <>
                      <label>{AppStrings.TRANSACTIONS_OVER}</label>
                      <input
                        type="number"
                        value={prevThreshold}
                        disabled
                        placeholder="Previous"
                      />
                    </>
                  )}
                  <label>{AppStrings.PAYMENT_IS_PROCESSED_BY}</label>
                  <select
                    value={rule.processor}
                    onChange={(e) => handleProcessorChange(e.target.value)}
                    disabled={!canEditTransactionRouting}
                  >
                    {cardActiveProcessors.map((processor) => (
                      <option key={processor.code} value={processor.code}>
                        {processor.label}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
        )}
                <p className="discover-routing-rule">
                  All Discover card deposits route through NMI
                </p>
                <SaveButton
                  isApiRunning={isApiRunning}
                  isDisabled={activeProcessorCodes.length < 2 || !canEditTransactionRouting}
                  callbackFunction={savePaymentProcessor}
                />
              </>
            )}
          </>
        )}

        {/* Hold the tab on a loader until the flag resolves — rendering the
            legacy editor early would expose hard-coded defaults (and a live
            Save) that may not be the UI this operator should see. */}
        {activeRoutingTab === "apple-pay" &&
          (applePayFlagLoading ? (
            <div className="loading-container">
              <RebetLoader height="100px" width="100px" />
            </div>
          ) : isApplePayV2RoutingEnabled ? (
            <ApplePayV2Routing />
          ) : (
          <>
            <h2>{AppStrings.APPLE_PAY_ROUTING}</h2>
            {isApplePayLoading ? (
              <div className="apple-pay-loading">Loading Apple Pay settings…</div>
            ) : (
              <>
                <div className="both-settings">
                  <div className="condition-section">
                    <label>{AppStrings.IF_PURCHASE_IS}</label>
                    <select value="less" disabled>
                      <option value="less">{AppStrings.LESS}</option>
                    </select>
                    <label>{AppStrings.THAN}</label>
                    <input
                      type="number"
                      value={applePayThreshold}
                      onChange={(e) => {
                        setApplePayThreshold(e.target.value);
                        setApplePayErrorMessage("");
                      }}
                      placeholder={AppStrings.AMOUNT}
                      disabled={!canEditTransactionRouting}
                    />
                    <label>,</label>
                    <label>{AppStrings.PAYMENT_IS_PROCESSED_BY}</label>
                    <select
                      value={applePayProviderBelow}
                      onChange={(e) => setApplePayProviderBelow(e.target.value)}
                      disabled={!canEditTransactionRouting}
                    >
                      {applePayProviderOptions.map((p) => (
                        <option key={p.code} value={p.code}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="condition-section">
                    <label>{AppStrings.AND_IF_PURCHASE_IS}</label>
                    <select value="greater" disabled>
                      <option value="greater">{AppStrings.GREATER}</option>
                    </select>
                    <label>{AppStrings.THAN}</label>
                    <input
                      type="number"
                      value={applePayThreshold}
                      onChange={(e) => {
                        setApplePayThreshold(e.target.value);
                        setApplePayErrorMessage("");
                      }}
                      placeholder={AppStrings.AMOUNT}
                      disabled={!canEditTransactionRouting}
                    />
                    <label>,</label>
                    <label>{AppStrings.PAYMENT_IS_PROCESSED_BY}</label>
                    <select
                      value={applePayProviderAbove}
                      onChange={(e) => setApplePayProviderAbove(e.target.value)}
                      disabled={!canEditTransactionRouting}
                    >
                      {applePayProviderOptions.map((p) => (
                        <option key={p.code} value={p.code}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <SaveButton
                  isApiRunning={isApplePaySaving}
                  isDisabled={!canEditTransactionRouting}
                  callbackFunction={saveApplePayRouting}
                />
              </>
            )}
          </>
          ))}
      </div>
    </div>
  );
};

export default PaymentToggle;
